<template>
	<v-app class="overflow-y-hidden">
		<v-app-bar
			v-if="!$route.meta?.hideChrome"
			id="dactyl-header"
			:class="useCompactHeader ? 'dactyl-header' : ''"
			color="white"
			:height="useCompactHeader ? '50' : '84'"
			:style="useCompactHeader ? {} : { padding: '0px 16px' }"
		>
			<div class="d-flex align-start">
				<v-img
					alt="Dactyl Logo"
					class="shrink cursor-pointer"
					src="@/assets/images/dactyl_logo.png"
					transition="scale-transition"
					:width="useCompactHeader ? '48' : '56'"
					v-bind="useCompactHeader ? { height: '35' } : {}"
					eager
					@click="redirectToDashboard"
				/>
			</div>

			<h3
				v-if="useCompactHeader"
				class="dactyl-logo-text font-weight-bold text-primary"
			>
				DACTYL
			</h3>
			<h1 v-else class="dactyl-logo-text font-weight-bold text-primary">
				DACTYL
			</h1>

			<div
				:class="
					useCompactHeader
						? 'd-sm-block d-none ml-1 text-primary'
						: 'd-sm-block d-none mx-3 text-primary'
				"
			>
				by
			</div>

			<div class="d-sm-flex d-sm-flex d-none align-start">
				<v-img
					alt="Human Predictions Logo"
					:class="useCompactHeader ? 'mr-2' : 'shrink mr-2 mb-2'"
					src="@/assets/images/human-predictions-logo-150px.png"
					transition="scale-transition"
					eager
					:width="useCompactHeader ? '110' : '150'"
					v-bind="useCompactHeader ? { height: '35' } : {}"
				/>
			</div>
			<v-spacer />

			<v-container
				v-if="isAuthenticated"
				class="d-flex justify-end pa-0 align-center"
				height="100%"
			>
				<v-btn
					v-if="readOnly || qcReadOnly || inSdeReadOnly"
					class="bg-primary mr-1"
					elevation="2"
					:disabled="saving"
					:loading="saving"
					@click="returnToDashboard"
				>
					Return to dashboard
				</v-btn>

				<v-btn
					v-if="inTraining || inDataEntry"
					class="bg-primary mr-2"
					elevation="2"
					:disabled="saving || validating || dataEntryNotLoaded"
					:loading="saving || validating"
					@click="triggerDataEntryValidate"
				>
					Validate
				</v-btn>

				<v-btn
					v-if="inTraining || inDataEntry"
					class="bg-primary mr-2"
					elevation="2"
					:disabled="saving || dataEntryNotLoaded"
					:loading="saving"
					@click="triggerDataEntrySave"
				>
					Save
				</v-btn>

				<v-btn
					v-if="inSde && !inSdeReadOnly"
					class="bg-primary mr-2"
					elevation="2"
					:disabled="saving || validating"
					:loading="validating"
					@click="triggerSdeValidate"
				>
					Validate
				</v-btn>

				<v-btn
					v-if="inSde && !inSdeReadOnly"
					class="bg-primary mr-2"
					elevation="2"
					:disabled="saving || validating"
					:loading="saving || validating"
					@click="triggerSdeSave"
				>
					Save
				</v-btn>

				<v-btn
					v-if="inSde && !inSdeReadOnly"
					class="bg-primary mr-2"
					elevation="2"
					:disabled="saving || validating"
					:loading="saving || validating"
					@click="triggerSdeSaveExit"
				>
					Save & Close
				</v-btn>

				<v-btn
					v-if="inSde && !inSdeReadOnly"
					class="bg-primary mr-2"
					elevation="2"
					:disabled="saving || validating"
					:loading="saving || validating"
					@click="sendSdeComplete"
				>
					Save & Mark Completed
				</v-btn>

				<v-btn
					v-if="inQualityControl && !qcReadOnly"
					class="bg-primary mr-2"
					elevation="2"
					:disabled="qcSaving || qcFinalizing"
					:loading="qcSaving"
					@click="triggerQualityControlSave"
				>
					Save
				</v-btn>

				<v-btn
					v-if="inTraining || inDataEntry"
					class="bg-primary mr-2"
					elevation="2"
					:disabled="saving || dataEntryNotLoaded"
					:loading="saving"
					@click="triggerSaveExit"
				>
					Save & Close
				</v-btn>

				<v-btn
					v-if="inQualityControl && !qcReadOnly"
					class="bg-primary mr-2"
					elevation="2"
					:disabled="qcSaving || qcFinalizing"
					:loading="qcSaving"
					@click="triggerQualityControlSaveExit"
				>
					Save & Close
				</v-btn>

				<v-dialog
					v-if="
						!inTraining && (inDataEntry || (inQualityControl && !qcReadOnly))
					"
					v-model="saveAndCompleteDialog"
					max-width="600"
				>
					<template #activator="{ props }">
						<v-tooltip :disabled="!qcCompleteBlocked" location="bottom">
							<template #activator="{ props: tipProps }">
								<div v-bind="tipProps" class="d-inline-block">
									<v-btn
										v-bind="props"
										class="bg-primary"
										:class="{ 'mr-2': isAdmin }"
										:disabled="
											saving ||
											qcSaving ||
											qcFinalizing ||
											qcCompleteBlocked ||
											dataEntryNotLoaded
										"
										:loading="saving || qcSaving || qcFinalizing"
										elevation="2"
									>
										{{
											isReworkSde ? "Save & Resubmit" : "Save & Mark Completed"
										}}
									</v-btn>
								</div>
							</template>
							Complete all QC stages before marking as completed
						</v-tooltip>
					</template>

					<v-card class="pb-5" :disabled="!!saving" :loading="saving">
						<v-toolbar color="primary" dark flat>
							<v-toolbar-title>
								{{ isReworkSde ? "Save & Resubmit" : "Save & Mark Completed" }}
							</v-toolbar-title>

							<v-spacer />

							<v-tooltip location="bottom">
								<template #activator="{ props }">
									<v-btn v-bind="props" icon size="large" @click="close">
										<v-icon>mdi-close</v-icon>
									</v-btn>
								</template>

								<span>Close</span>
							</v-tooltip>
						</v-toolbar>

						<v-card-text class="pa-10 text-center text-subtitle-1">
							{{
								isReworkSde
									? 'Click "Resubmit" to save your changes and send them back to the QC reviewer.'
									: saveAndCompleteText
							}}
						</v-card-text>

						<v-card-actions>
							<v-spacer />

							<v-btn
								v-if="inDataEntry"
								class="mr-3 bg-secondary"
								elevation="2"
								@click="triggerSaveDone"
							>
								{{ isReworkSde ? "Resubmit" : "Mark Complete" }}
							</v-btn>
							<v-btn
								v-if="inQualityControl"
								class="mr-3 bg-secondary"
								elevation="2"
								@click="triggerSaveDoneQC"
							>
								Mark Complete
							</v-btn>
							<v-btn class="mr-3 bg-grey" elevation="2" @click="close">
								Cancel
							</v-btn>

							<v-spacer />
						</v-card-actions>
					</v-card>
				</v-dialog>

				<v-dialog
					v-if="((inSde && !inSdeReadOnly) || inDataEntry) && isAdmin"
					v-model="discardDataDialog"
					max-width="600"
				>
					<template #activator="{ props }">
						<v-btn
							v-bind="props"
							:disabled="saving || validating || dataEntryNotLoaded"
							:loading="saving || validating"
							class="bg-primary"
							elevation="2"
						>
							Discard Data
						</v-btn>
					</template>

					<v-card class="pb-5" :disabled="!!saving" :loading="saving">
						<v-toolbar color="primary" dark flat>
							<v-toolbar-title> Discard Extracted Data </v-toolbar-title>
							<v-spacer />
							<v-tooltip location="bottom">
								<template #activator="{ props }">
									<v-btn
										v-bind="props"
										icon
										size="large"
										@click="closeDiscardDialog"
									>
										<v-icon>mdi-close</v-icon>
									</v-btn>
								</template>

								<span>Close</span>
							</v-tooltip>
						</v-toolbar>

						<v-card-text class="pa-10 text-center text-subtitle-1">
							Are you sure you want to discard extracted data in this paper?
							This action cannot be undone.
						</v-card-text>

						<v-card-actions>
							<v-spacer />

							<v-btn
								class="bg-secondary"
								elevation="2"
								@click="triggerDiscard"
							>
								Discard Extracted Data
							</v-btn>

							<v-btn elevation="2" @click="closeDiscardDialog"> Cancel </v-btn>

							<v-spacer />
						</v-card-actions>
					</v-card>
				</v-dialog>

				<v-dialog v-if="inTraining" v-model="resetDataDialog" max-width="600">
					<template #activator="{ props }">
						<v-btn
							v-bind="props"
							:disabled="saving || dataEntryNotLoaded"
							:loading="saving"
							class="bg-primary"
							elevation="2"
						>
							Reset Data
						</v-btn>
					</template>

					<v-card class="pb-5" :disabled="!!saving" :loading="saving">
						<v-toolbar color="primary" dark flat>
							<v-toolbar-title>Reset Data</v-toolbar-title>
							<v-spacer />
							<v-tooltip location="bottom">
								<template #activator="{ props }">
									<v-btn
										v-bind="props"
										icon
										size="large"
										@click="closeResetDialog"
									>
										<v-icon>mdi-close</v-icon>
									</v-btn>
								</template>

								<span>Close</span>
							</v-tooltip>
						</v-toolbar>

						<v-card-text class="pa-10 text-center text-subtitle-1">
							Are you sure you want to reset extracted data in this training
							paper? This action cannot be undone.
						</v-card-text>

						<v-card-actions>
							<v-spacer />

							<v-btn
								class="bg-secondary"
								elevation="2"
								@click="triggerReset"
							>
								Reset Data
							</v-btn>

							<v-btn elevation="2" @click="closeResetDialog"> Cancel </v-btn>

							<v-spacer />
						</v-card-actions>
					</v-card>
				</v-dialog>

				<v-divider
					v-if="inTraining || inSde || inDataEntry || inQualityControl"
					class="mx-5"
					inset
					vertical
				/>
			</v-container>

			<notification-bell v-if="isAuthenticated && qcDashboardEnabled" />
			<v-divider
				v-if="isAuthenticated && qcDashboardEnabled"
				class="mx-5"
				inset
				vertical
			/>

			<v-login
				v-if="!isAuthenticated && !$route.meta?.hideChrome"
				ref="login"
				v-model="login"
				@activate-activity-timer="activateActivityTracker"
			/>

			<div v-else :class="{ 'mr-4': useCompactHeader }">
				<v-logout
					ref="logout"
					@deactivate-activity-timer="deactivateActivityTracker"
				/>
			</div>

			<v-dialog v-model="userActivityDialog" max-width="600">
				<v-card class="pb-5">
					<v-toolbar color="primary" dark flat>
						<v-toolbar-title>Session timeout</v-toolbar-title>
					</v-toolbar>

					<v-spacer />

					<v-card-text class="pa-10 text-center">
						Your session will timeout in
						{{ countdownString }}.
						<br />
						<br />
						Are you still there?
					</v-card-text>

					<v-card-actions>
						<v-spacer />

						<v-btn
							class="bg-secondary"
							elevation="2"
							@click="userStillActive"
						>
							Yes, I am
						</v-btn>

						<v-spacer />
					</v-card-actions>
				</v-card>
			</v-dialog>

			<user-manual-dialog ref="userManual" />
		</v-app-bar>

		<v-notification />

		<v-main>
			<!-- DCTL-2184: loud, persistent "viewing as" banner. bg-error (red) is
			deliberately distinct from the brand primary/secondary so an admin can
			never forget they are seeing another user's view. -->
			<v-banner
				v-if="isAuthenticated && isImpersonating"
				class="bg-error text-white"
				lines="one"
				role="status"
				aria-live="polite"
			>
				<template #prepend>
					<v-icon
						color="white"
						icon="mdi-account-eye"
						size="28"
						aria-hidden="true"
					></v-icon>
				</template>
				<template #text>
					<span class="font-weight-medium">
						You are viewing as {{ impersonatedUserLabel }}
					</span>
				</template>
				<template #actions>
					<v-btn
						class="bg-white text-primary"
						elevation="2"
						:loading="exitingImpersonation"
						@click="exitImpersonation"
					>
						Exit
					</v-btn>
				</template>
			</v-banner>

			<v-banner
				v-if="isAuthenticated && showBanner"
				class="bg-primary cookie-banner"
				lines="one"
				:text="systemMessage"
			>
				<template #prepend>
					<v-icon color="secondary" icon="mdi-cookie" size="30"></v-icon>
				</template>
				<template #actions>
					<v-btn
						class="mb-1 bg-secondary"
						elevation="2"
						@click="dismissBanner('cookie')"
					>
						Acknowledge
					</v-btn>
				</template>
			</v-banner>

			<router-view
				:key="routerViewKey"
				ref="router"
				v-model:login="login"
				:class="useCompactHeader ? 'pb-0' : ''"
				:is-admin="isAdmin"
				:show-banner="showBanner"
				@token="handleToken"
				@activate-activity-timer="activateActivityTracker"
				@deactivate-activity-timer="deactivateActivityTracker"
				@check-dirty="(checkDirtyMethod) => (checkDirty = checkDirtyMethod)"
				@handle-logout-save="
					(logoutSaveMethod) => (logoutSave = logoutSaveMethod)
				"
				@load-state="(loadStateMethod) => (pageLoadState = loadStateMethod)"
			/>
		</v-main>
	</v-app>
</template>

<script>
import _ from "lodash";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import auth from "@/auth";
import { stopImpersonation } from "@/utils/impersonation";
import { sessionTimerAction, TIMER_ACTIONS } from "@/utils/sessionTimeout";
import { abortAllInflight } from "@/utils/inflightRequests";
import Timer from "@/assets/scripts/session-timer.worker.js?worker";

import VLogin from "@/components/VLogin";
import VLogout from "@/components/VLogout";
import VNotification from "@/components/VNotification";
import NotificationBell from "@/components/NotificationBell.vue";
import UserManualDialog from "@/pages/UserManualDialog";
import { useLDFlag } from "launchdarkly-vue-client-sdk";
import * as Sentry from "@sentry/vue";

const cookieCheckInterval = 30000; // 30 sec
const countdownWait = 300000; // 5 mins
const userActivityDebounceWait = 500; // 0.5 sec
const userInactiveWait = 600000; // 10 mins

// Prevent vue3 ResizeObserver errors with a debounce
const debounce = (callback, delay) => {
	let tid;
	return function (...args) {
		const ctx = this;
		if (tid) clearTimeout(tid);
		tid = setTimeout(() => {
			callback.apply(ctx, args);
		}, delay);
	};
};

const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class extends OriginalResizeObserver {
	constructor(callback) {
		super(debounce(callback, 20));
	}
};

export default {
	name: "App",

	components: {
		UserManualDialog,
		NotificationBell,
		VLogin,
		VLogout,
		VNotification,
	},

	setup() {
		const qcDashboardEnabled = useLDFlag("qc-dashboard");
		return {
			qcDashboardEnabled,
		};
	},

	data() {
		return {
			axiosInterceptor: null,
			checkDirty: null,
			// DCTL-2184: guards the impersonation banner's Exit button while the
			// stop request + re-scope are in flight.
			exitingImpersonation: false,
			confirmationText: "Are you sure you want to log out?",
			countdown: -1,
			countdownInterval: null,
			currentDialog: {},
			discardDataDialog: false,
			// elements that are targets for dialog dragging
			draggableClasses: ["drag-handle"],
			triggerLogin: false,
			login: false,
			logoutSave: null,
			// callback emitted by the DataEntry page (same pattern as
			// checkDirty/logoutSave): returns true once the page's own
			// document has fully loaded. Save-shaped app-bar buttons stay
			// disabled while it returns false so a pre-load click can never
			// serialize an empty or stale document.
			pageLoadState: null,
			resetDataDialog: false,
			sessionBroadcast: null,
			timer: null,
			saveAndCompleteDialog: false,
			saveAndCompleteText: `
				Click “Mark Complete” to finish this document. You will not be able to edit this document any longer.
			`,
			sessionInterval: null,
			sessionLastChecked: null,
			systemMessage: `
				Dactyl uses cookies to improve user experience. By continuing to use this website, you agree to our use of
				Strictly Necessary cookies.
			`,
			userActivityDialog: false,
			userActivityTimeout: null,
		};
	},

	computed: {
		countdownString() {
			return this.millisecondsToMinutesAndSeconds(this.countdown);
		},
		// True while the routed DataEntry page is on-screen but has not
		// finished loading its own document. Reads the page's reactive load
		// flag through the emitted callback, so this recomputes when the page
		// finishes loading.
		dataEntryNotLoaded() {
			return (
				(this.inDataEntry || this.inTraining) &&
				typeof this.pageLoadState === "function" &&
				!this.pageLoadState()
			);
		},
		// Keys the routed page so that navigating between documents on the SAME
		// route record (a param-only change of the identifying uuid) force-
		// remounts the page instead of reusing it - a reused page keeps document
		// A's state/baseline/autosave while its props flip to B, and saves then
		// post A-shaped writes against B. The identifying param is :sdeUuid for
		// the SDE editor/viewer routes and :qcUuid for the QC dashboard route
		// (the notification bell navigates QC record -> QC record this way).
		// Keyed on the matched route record plus that uuid (NOT fullPath): a
		// dataPointUuid- or query-only change within the same document must not
		// remount.
		routerViewKey() {
			const record = this.$route.matched[0]?.path ?? this.$route.path;
			const params = this.$route.params ?? {};
			const identity = params.sdeUuid ?? params.qcUuid;
			return identity ? `${record}::${identity}` : record;
		},
		inDataEntry() {
			return this.$route.path.startsWith("/data-entry/");
		},
		isReworkSde() {
			return this.inDataEntry && useMainStore().isReworkSde;
		},
		inQualityControl() {
			return this.$route.path.startsWith("/quality-control");
		},
		useCompactHeader() {
			// Compact whenever we're in the SDEv2 editor/route. This keys off the
			// route, not the sde-v2 flag: a v2 extraction now opens the SDEv2
			// editor via its sticky stored format even when the flag is off, and
			// the header must shrink in that case too.
			return (
				this.inSde ||
				(this.qcDashboardEnabled && this.inQualityControl) ||
				this.inSdeUserManual
			);
		},
		isAdmin() {
			return useMainStore().isAdmin;
		},
		inSde() {
			return this.$route.path.startsWith("/source-data-extraction");
		},
		inSdeReadOnly() {
			return this.$route.path.startsWith("/source-data-extraction-read-only");
		},
		inSdeUserManual() {
			return this.$route.path.startsWith("/sde-user-manual");
		},
		inTraining() {
			return this.$route.path.startsWith("/data-entry-training");
		},
		readOnly() {
			return this.$route.path.startsWith("/data-entry-read-only");
		},
		help() {
			return useMainStore().help;
		},
		isAuthenticated() {
			return useMainStore().isAuthenticated;
		},
		// DCTL-2184: true while an admin is viewing the app as another user.
		isImpersonating() {
			return useMainStore().isImpersonating;
		},
		// The user currently being viewed as (store.user IS the target while
		// impersonating). Falls back to email when the name is blank.
		impersonatedUserLabel() {
			const u = useMainStore().user;
			const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
			return name || u.email;
		},
		qcCompleteBlocked() {
			return this.inQualityControl && !useMainStore().qcAllStepsCompleted;
		},
		qcReadOnly() {
			return this.inQualityControl && useMainStore().qcReadOnly;
		},
		qcSaving() {
			return useMainStore().qcSave;
		},
		qcFinalizing() {
			return (
				this.inQualityControl &&
				useMainStore().isQcFinalizing(this.$route.params.qcUuid)
			);
		},
		saving() {
			return useMainStore().saveInProgress;
		},
		user() {
			return useMainStore().user;
		},
		validating() {
			return useMainStore().validate;
		},
		showBanner() {
			// The Provenance Viewer's first-visit legal gate already obtains
			// consent covering Strictly Necessary cookies, so the separate cookie
			// banner is redundant on that surface. Prospect-only users also can't
			// dismiss it - /users/record-action is outside their containment
			// allowlist (ProspectContainment), so Acknowledge would silently 403.
			if (useMainStore().isProspect) {
				return false;
			}
			if (this.$route?.path?.startsWith("/provenance-viewer/")) {
				return false;
			}
			return !this.user.cookie_notice_dismissed_at;
		},
	},

	watch: {
		help(val) {
			if (val) {
				this.$refs.userManual.open(val);
			} else {
				// close the manual
				this.$refs.userManual.close();
			}
		},
	},

	mounted() {
		// Set up global axios interceptor to catch 401s (expired sessions)
		this.axiosInterceptor = this.$http.interceptors.response.use(
			(response) => response,
			async (error) => {
				// If backend session expired (401) but frontend cookie still exists
				if (error.response?.status === 401 && this.isAuthenticated) {
					console.warn(
						"401 detected with valid frontend cookie - clearing auth state",
					);

					// Clear frontend auth state
					auth.clearAuthenticated();
					this.setAuthenticated(false);
					this.reset();
					Sentry.setUser(null);

					// Redirect to login, preserving intended destination
					const currentPath = this.$route.path;
					if (currentPath !== "/login" && currentPath !== "/") {
						this.$router.push({
							path: "/login",
							query: { redirect: currentPath },
						});
					} else {
						this.$router.push("/login");
					}
				}

				return Promise.reject(error);
			},
		);

		// Record the active session, then ping minutely to record activity
		// this.activityTimer = setInterval(() => {
		// 	this.$http.post("/users/record-active-session");
		// }, 60000);

		this.sessionLastChecked = new Date().getTime();

		// check auth cookie at regular interval
		this.sessionInterval = setInterval(
			() => this.checkCookie(),
			cookieCheckInterval,
		);

		if (!this.isAuthenticated) {
			// do not run the user activity tracker when not authenticated
			this.deactivateActivityTracker();
		} else {
			// refresh the session
			this.refreshSession();

			// start the user activity tracker
			this.activateActivityTracker();
		}

		// initialize cross-tab session broadcast channel
		try {
			this.sessionBroadcast = new BroadcastChannel("dactyl-session");
			this.sessionBroadcast.onmessage = (event) => {
				const type = event?.data?.type;
				if (!type) return;
				if (type === "user_activity") {
					this.onCrossTabActivity();
				} else if (type === "user_still_active") {
					this.onCrossTabUserStillActive();
				}
			};
		} catch (_) {
			// ignore if not supported
		}

		// make vuetify dialogs draggable
		this.initializeDialogDrag();
	},

	beforeUnmount() {
		this.deactivateActivityTracker();

		// Clean up axios interceptor
		if (this.axiosInterceptor !== null) {
			this.$http.interceptors.response.eject(this.axiosInterceptor);
		}

		this.sessionInterval = null;
		try {
			this.sessionBroadcast?.close();
		} catch (e) {
			// ignore errors closing broadcast channel
		}
	},

	methods: {
		...mapActions(useMainStore, [
			"clearHelp",
			"reset",
			"sendValidate",
			"sendSave",
			"sendSdeComplete",
			"setAuthenticated",
			"setUser",
			"showNotification",
		]),
		// DCTL-2184: exit impersonation, restore the admin identity, and re-scope
		// the SPA back to the admin. stopImpersonation re-reads /users/me +
		// permissions; then land on /home so the admin isn't left on a
		// target-scoped route.
		async exitImpersonation() {
			this.exitingImpersonation = true;
			try {
				await stopImpersonation();
				this.$router.push("/home");
			} catch (error) {
				console.error(error);
				this.showNotification({
					message: "Could not exit impersonation. Try again.",
					status: "error",
				});
			} finally {
				this.exitingImpersonation = false;
			}
		},
		close: function () {
			this.saveAndCompleteDialog = false;
		},
		closeDiscardDialog: function () {
			this.discardDataDialog = false;
		},
		closeResetDialog: function () {
			this.resetDataDialog = false;
		},
		redirectToDashboard() {
			this.$router.push("/home/dashboard");
		},
		redirectToMyCompletedExtractions() {
			this.$router.push("/home/my-completed-extractions");
		},
		returnToDashboard() {
			// QC read-only sessions return to the QC dashboard; SDE read-only
			// sessions return to "My completed extractions" (legacy
			// behavior). Both paths route through the same toolbar button so
			// reviewers don't need to learn separate exit affordances.
			if (this.qcReadOnly) {
				this.$router.push("/home/quality-control-dashboard");
			} else {
				this.$router.push("/home/my-completed-extractions");
			}
		},
		dialogMousedownHandler(event) {
			const closestDialog = event.target.closest(".v-overlay__content");
			if (
				event.button === 0 &&
				closestDialog != null &&
				this.isDraggable(event.target)
			) {
				event.target.classList.add("grabbing");

				// element that should be moved
				this.currentDialog.el = closestDialog;

				// element that was clicked on
				this.currentDialog.handle = event.target;

				this.currentDialog.mouseStartX = event.clientX;
				this.currentDialog.mouseStartY = event.clientY;

				this.currentDialog.elStartX =
					this.currentDialog.el.getBoundingClientRect().left;
				this.currentDialog.elStartY =
					this.currentDialog.el.getBoundingClientRect().top;

				this.currentDialog.el.style.margin = 0;
				this.currentDialog.el.style.position = "fixed";
				this.currentDialog.el.style.transition = "none";

				this.currentDialog.oldTransition =
					this.currentDialog.el.style.transition;
			}
		},
		dialogMousemoveHandler(event) {
			if (this.currentDialog.el === undefined) {
				return;
			}

			this.currentDialog.el.style.left =
				Math.min(
					Math.max(
						this.currentDialog.elStartX +
							event.clientX -
							this.currentDialog.mouseStartX,
						0,
					),
					window.innerWidth -
						this.currentDialog.el.getBoundingClientRect().width,
				) + "px";
			this.currentDialog.el.style.top =
				Math.min(
					Math.max(
						this.currentDialog.elStartY +
							event.clientY -
							this.currentDialog.mouseStartY,
						0,
					),
					window.innerHeight -
						this.currentDialog.el.getBoundingClientRect().height,
				) + "px";

			this.currentDialog.handle.classList.add("grabbing");
		},
		dialogMouseupHandler() {
			if (this.currentDialog.el === undefined) {
				return;
			}

			this.currentDialog.el.style.transition = this.currentDialog.oldTransition;
			this.currentDialog.el.dispatchEvent(new CustomEvent("drag-end"));
			this.currentDialog.el = undefined;

			this.currentDialog.handle.classList.remove("grabbing");
		},
		initializeDialogDrag() {
			document.removeEventListener("mousedown", this.dialogMousedownHandler);
			document.removeEventListener("mousemove", this.dialogMousemoveHandler);
			document.removeEventListener("mouseup", this.dialogMouseupHandler);

			document.addEventListener("mousedown", this.dialogMousedownHandler);
			document.addEventListener("mousemove", this.dialogMousemoveHandler);
			document.addEventListener("mouseup", this.dialogMouseupHandler);

			setInterval(() => {
				// prevent out of bounds
				const dialog = document.querySelector(".v-dialog.v-dialog--active");

				if (dialog === null) {
					return;
				}

				dialog.style.left =
					Math.min(
						parseInt(dialog.style.left, 10),
						window.innerWidth - dialog.getBoundingClientRect().width,
					) + "px";
				dialog.style.top =
					Math.min(
						parseInt(dialog.style.top, 10),
						window.innerHeight - dialog.getBoundingClientRect().height,
					) + "px";
			}, 100);
		},
		isDraggable(element) {
			// Check the element and all parents up to the dialog
			let current = element;
			while (current && !current.classList.contains("v-overlay__content")) {
				for (const draggableClass of this.draggableClasses) {
					if (current.classList.contains(draggableClass)) {
						return true;
					}
				}
				current = current.parentElement;
			}

			return false;
		},
		triggerDataEntryValidate: function () {
			this.sendValidate();
		},
		triggerDataEntrySave: function () {
			this.sendSave({ type: "data_entry" });
		},
		triggerSdeSave: function () {
			this.sendSave({ type: "sde" });
		},
		triggerSdeValidate: function () {
			this.sendValidate();
		},
		triggerQualityControlSave: function () {
			this.sendSave({ type: "quality_control" });
		},
		triggerSaveExit: function () {
			this.sendSave({
				exit: true,
				type: "data_entry",
			});
		},
		triggerSdeSaveExit: function () {
			this.sendSave({
				exit: true,
				type: "sde",
			});
		},
		triggerQualityControlSaveExit: function () {
			this.sendSave({
				exit: true,
				type: "quality_control",
			});
		},
		triggerSaveDone: function () {
			this.close();

			this.sendSave({
				done: true,
				exit: true,
				type: "data_entry",
			});
		},
		triggerSaveDoneQC: function () {
			this.close();

			this.sendSave({
				done: true,
				exit: true,
				type: "quality_control",
			});
		},
		startSessionTimeoutCountdown: function () {
			// stop auto-save timer if user currently in data extraction
			if (this.inDataEntry) {
				const currentComponent =
					this.$router?.currentRoute?.value?.matched[0]?.components?.default;

				if (currentComponent) {
					currentComponent.methods.deactivateAutoSave();
				}
			}
			this.countdown = countdownWait;

			this.userActivityDialog = true;

			this.timer.postMessage({
				action: "start_countdown",
				duration: countdownWait,
				interval: 1000,
			});
		},
		resetUserActivityTimer: _.debounce(function () {
			// if past time threshold since last check, check auth cookie
			const elapsed = new Date().getTime() - this.sessionLastChecked;
			if (elapsed > cookieCheckInterval) {
				this.checkCookie();
			}

			this.timer?.postMessage({
				action: "start_timer",
				// wait for 10 mins before starting the dialog countdown
				duration: userInactiveWait,
			});
			// notify other tabs to keep their timers alive
			try {
				this.sessionBroadcast?.postMessage({ type: "user_activity" });
			} catch (e) {
				// ignore if broadcast channel not available
			}
		}, userActivityDebounceWait), // start inactivity timer 5 secs after last movement
		checkCookie: function () {
			// skip cookie check for unauthed routes
			if (this.$route.meta && this.$route.meta.guest) {
				return;
			}

			this.sessionLastChecked = new Date().getTime();

			this.$http
				.get("/ping", { headers: { "Cache-Control": "no-store" } })
				.finally(async () => {
					if (!auth.isAuthenticated()) {
						// Prospects (DCTL-1951) ride a 30-day persistent session; a
						// transient/missing client flag cookie must NOT force them
						// out. Genuine staleness is caught by the real-401 axios
						// interceptor in mounted(), not this client-cookie
						// heuristic. Non-prospect behavior is unchanged.
						if (useMainStore().isProspect) {
							return;
						}
						this.setAuthenticated(false);
						await this.autoLogout();
					}
				});
		},
		iframeMessageHandler: function (event) {
			if (["mousemove", "scroll", "keydown", "resize"].includes(event.data)) {
				this.resetUserActivityTimer();
			}
		},
		refreshSessionTimeoutCountdown: async function () {
			this.countdown -= 1000;

			if (this.countdown < 1) {
				await this.autoLogout();
			}
		},
		activateActivityTracker: function () {
			// Prospects (DCTL-1951) run on a 30-day persistent session and must
			// NOT be subject to the idle-timeout auto-logout. Skip the tracker
			// entirely: no idle web-worker Timer and no activity listeners. Their
			// session staleness is handled by a real 401 from an authenticated
			// request (the mounted() axios interceptor), not this client-side idle
			// countdown. Non-prospect behavior is unchanged.
			if (useMainStore().isProspect) {
				return;
			}

			this.deactivateActivityTracker();

			// create the worker
			this.timer = new Timer();

			// listen for timer messages
			this.timer.onmessage = ({ data }) => {
				// if not authenticated, stop the timer and close the dialog
				if (!this.isAuthenticated) {
					this.deactivateActivityTracker();
					this.userActivityDialog = false;
					return;
				}

				switch (sessionTimerAction(data)) {
					// inactivity threshold reached, or overshot while the tab was
					// suspended: either way the user gets the countdown dialog
					case TIMER_ACTIONS.START_COUNTDOWN:
						this.startSessionTimeoutCountdown();
						break;
					case TIMER_ACTIONS.TICK:
						this.refreshSessionTimeoutCountdown();
						break;
					// the countdown itself overshot: the session is gone
					case TIMER_ACTIONS.LOGOUT:
						this.countdown = 0;
						this.refreshSessionTimeoutCountdown();
						break;
				}
			};

			window.addEventListener("mousemove", this.resetUserActivityTimer);
			window.addEventListener("scroll", this.resetUserActivityTimer);
			window.addEventListener("keydown", this.resetUserActivityTimer);
			window.addEventListener("resize", this.resetUserActivityTimer);

			window.addEventListener("message", this.iframeMessageHandler);
		},
		deactivateActivityTracker: function () {
			window.removeEventListener("mousemove", this.resetUserActivityTimer);
			window.removeEventListener("scroll", this.resetUserActivityTimer);
			window.removeEventListener("keydown", this.resetUserActivityTimer);
			window.removeEventListener("resize", this.resetUserActivityTimer);

			window.removeEventListener("message", this.iframeMessageHandler);

			// clear session timeout and countdown
			this.timer?.postMessage({ action: "stop_all" });

			// terminate the worker
			this.timer?.terminate();
			this.timer = null;
		},
		onCrossTabActivity: function () {
			const elapsed = new Date().getTime() - this.sessionLastChecked;
			if (elapsed > cookieCheckInterval) {
				this.checkCookie();
			}
			this.timer?.postMessage({
				action: "start_timer",
				duration: userInactiveWait,
			});
		},
		onCrossTabUserStillActive: function () {
			// Dismiss local dialog and fully reset timers
			this.userActivityDialog = false;
			this.timer?.postMessage({ action: "stop_all" });
			// Restart activity timer fresh
			this.resetUserActivityTimer();
		},
		userStillActive: function () {
			this.userActivityDialog = false;

			// refresh session
			this.refreshSession();

			// start auto-save timer if user currently in data extraction
			if (this.inDataEntry) {
				const currentComponent =
					this.$router?.currentRoute?.value?.matched[0]?.components?.default;

				if (currentComponent) {
					currentComponent.methods.activateAutoSave();
				}
			}

			// stop all clocks
			this.timer.postMessage({ action: "stop_all" });

			// notify other tabs to dismiss their countdown and reset timers
			this.sessionBroadcast?.postMessage({ type: "user_still_active" });

			// restart the activity timer
			this.resetUserActivityTimer();
		},
		refreshSession() {
			this.$http
				.post("/refresh-session")
				.catch((error) => console.error(error));
		},
		millisecondsToMinutesAndSeconds: function (ms) {
			if (ms < 0) {
				return "[invalid value]";
			} else {
				const minutes = Math.floor(ms / 60000);
				const seconds = ((ms % 60000) / 1000).toFixed(0);

				return seconds === 60
					? minutes + 1 + ":00"
					: minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
			}
		},
		dismissBanner(type) {
			if (type === "cookie") {
				// save user cookie banner dismissal
				this.$http
					.post("/users/record-action", { type })
					.then((response) => {
						const payload = response.data.payload;

						// update logged in user
						this.setUser(payload.user);
					})
					.catch(() => {
						// do not display error
					});
			}
		},
		handleToken(token) {
			this.$refs.login.login(token);
		},

		async triggerReset() {
			await new Promise(
				function (resolve) {
					this.sendSave({
						resolve: resolve,
						type: "reset_data_entry",
					});
				}.bind(this),
			);
			return this.closeResetDialog();
		},
		async triggerDiscard() {
			await new Promise(
				function (resolve) {
					this.sendSave({
						resolve: resolve,
						type: "discard_sde",
					});
				}.bind(this),
			);
			return this.closeDiscardDialog();
		},
		async triggerSave() {
			if (this.checkDirty) {
				const dirty = this.checkDirty();
				if (dirty) {
					//TODO: When QC is implemented, the new autosave behavior will need to be added via
					// a "checkDirty" and "logoutSave" function; leaving the previous code here as reference
					if (this.logoutSave) {
						await this.logoutSave();
					} else {
						return new Promise(
							function (resolve) {
								const payload = { resolve: resolve };
								if (this.inDataEntry) {
									payload.type = "data_entry";
								} else if (this.inQualityControl) {
									payload.type = "quality_control";
								}

								this.sendSave(payload);
							}.bind(this),
						);
					}
				}
			}

			return new Promise((resolve) => resolve());
		},
		async autoLogout() {
			// redirect to login page
			if (this.isAuthenticated) {
				try {
					// Try to save any unsaved changes, but don't block logout if it fails
					try {
						await this.triggerSave();
					} catch (saveError) {
						console.error(
							"Save failed during logout, continuing with logout:",
							saveError,
						);
					}

					// Attempt logout via VLogout component if available
					if (
						this.$refs.logout &&
						typeof this.$refs.logout.logout === "function"
					) {
						try {
							await this.$refs.logout.logout();
						} catch (logoutError) {
							console.error(
								"Logout component threw error:",
								logoutError.message,
							);
						}
					} else {
						console.error(
							"VLogout component not available, attempting backend logout",
						);
						// Same reason as VLogout.logout(): a response landing after
						// the logout response would re-set the session cookies.
						abortAllInflight();
						try {
							await this.$http.post("/users/logout");
						} catch (error) {
							console.error("Backend logout failed:", error.response?.status);
						}
					}
				} finally {
					Sentry.setUser(null);
					auth.clearAuthenticated();
					this.setAuthenticated(false);
					this.reset();

					this.userActivityDialog = false;
					this.timer?.postMessage({ action: "stop_all" });

					// hard navigation: a router push can be cancelled by a guard
					// and leave the old page rendering with a reset store
					// (DCTL-2654)
					auth.leaveToLogin();
				}
			} else {
				this.reset();
				this.userActivityDialog = false;
				this.timer?.postMessage({ action: "stop_all" });
				auth.leaveToLogin();
			}
		},
	},
};
</script>

<style lang="scss">
html {
	overflow-y: auto;
}

// PDF.js (via vue-pdf-embed-fork) appends a 0x0 hidden canvas to <body> for
// off-screen text extraction. Canvas defaults to display: inline, so body's
// inherited line-height (24px) creates a 24px line-box around the empty
// canvas, pushing document height past the viewport on full-bleed pages
// (notably SDEv1, where the iframe fills 100vh exactly).
canvas.hiddenCanvasElement {
	display: none;
}

.dactyl-logo-text {
	letter-spacing: 1px;
}

@mixin overrides {
	color: #f58634 !important;
	caret-color: #f58634 !important;
}

// override for some components that do not adhere to given options
.v-application .primary--text {
	&.v-input,
	&.v-list-item {
		@include overrides;

		.primary--text {
			@include overrides;
		}
	}
}

.v-data-table-header .text-center.sortable {
	padding-right: 2px;
}

.cursor-pointer {
	cursor: pointer;
}

.cursor-default {
	cursor: default !important;
}

.cursor-not-allowed {
	cursor: not-allowed !important;
}

.v-stepper.v-stepper--alt-labels {
	.v-stepper__label {
		text-align: center !important;
	}
}

.v-menu__content.theme--light.v-small-dialog__menu-content {
	margin-top: 30px;
}

.drag-handle {
	cursor: grab;

	&.grabbing {
		cursor: grabbing;
	}
}

.v-banner__actions {
	margin-bottom: 0px;
	margin-right: 8px;
}

.v-banner__prepend {
	height: 100%;
	display: flex;
	align-items: center;
}

.app-progress-container {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
}

.dactyl-header {
	padding: 0px 16px;
	box-shadow:
		0px 1px 2px -1px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
		0px 4px 5px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)) !important;
}

// Quick-resolve button sizing for merge conflict resolution (Graph & Table views)
.quick-resolve-label {
	font-size: 0.8rem;
	min-width: 28px !important;
	padding: 0 4px !important;
}

// A chosen tonal quick-resolve toggle (the graph row's Avg) takes a ring.
// The discard toggles are a solid fill when chosen, the one chosen style
// for keep / discard decisions, and need no ring.
.selected-resolve-btn.v-btn--variant-tonal {
	box-shadow: inset 0 0 0 1px currentColor;
}

// Consistent action button height across QC step toolbars
.step-action-btn {
	height: 36px !important;
}

// Soften default black ripple on light-themed components (tabs, steppers, buttons)
.v-ripple__animation {
	background: rgba(0, 0, 0, 0.12) !important;
}

// Force v-card to use the global on-surface theme color instead of its
// locally re-computed black (which ignores the custom on-surface setting).
.v-card {
	color: rgba(
		var(--v-theme-on-surface),
		var(--v-high-emphasis-opacity)
	) !important;
}
</style>
